import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ListOrdered } from 'lucide-react'
import { getServerNowMs } from '../../hooks/useServerClock.js'
import { liveBlockRundown } from '../../lib/livePartPlan.js'
import type { ScheduleItem } from '../../types/index.js'
import { LivePartAdjustDialog } from './LivePartAdjustDialog.js'
import './LivePartChrono.css'

/**
 * Chrono de la partie en cours d'un bloc de direct.
 *
 * Le bandeau de direct compte vers la fin du **bloc** — 19 h — alors que le
 * plateau, lui, raisonne en parties : « on rend l'antenne dans six minutes ».
 * Ce chrono rend ce repère à partir du déroulé estimé saisi sur l'élément
 * (`lib/livePartPlan.ts`).
 *
 * Il est explicitement **estimatif** et ne commande rien : aucune coupure,
 * aucun décrochage, aucune alerte n'en dépend. Le compteur peut donc atteindre
 * zéro sans que rien ne se passe — c'est précisément ce qu'on attend d'un
 * repère de conduite. La coupure reste le geste de l'opérateur, dans le bandeau
 * juste en dessous.
 *
 * Seules les durées sont estimées : le départ de chaque partie, lui, est
 * l'antenne réelle. La partie 1 court depuis le top, le compteur s'efface
 * (« —:— ») pendant le décrochage, et la partie 2 repart au raccrochage
 * (`liveBlockRundown`). Le temps du bloc, lui, reste celui de la grille : c'est
 * le bandeau juste en dessous qui le porte.
 *
 * La dernière partie laissée en automatique absorbe l'écart avec la fin du
 * bloc, recalculée à chaque décrochage. Avec `adjustable`, un clic sur le
 * chrono ouvre `LivePartAdjustDialog` pour fixer le temps des parties en cours
 * et à venir — ajustement de conduite, sans effet sur l'EPG.
 */

/** Sous cette marge, la partie touche à sa fin et le compteur le signale. */
const PART_ENDING_SOON_SEC = 60

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function clockAt(ms: number): string {
  return new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

/** « 8 min » : la durée annoncée d'une partie, lisible de loin. */
function minutesLabel(seconds: number): string {
  const minutes = Math.round(seconds / 60)
  return minutes >= 1 ? `${minutes} min` : `${Math.max(0, Math.round(seconds))} s`
}

const EMPTY_ITEMS: readonly ScheduleItem[] = []

/** « Partie 2/3 — Le débat » : le rang d'abord, il se lit de loin. */
function partLabel(number: number, count: number, title: string | null): string {
  return `Partie ${number}/${count}${title ? ` — ${title}` : ''}`
}

export const LivePartChrono = memo(function LivePartChrono(
  // `items` porte les autres segments du bloc : sans elle, le déroulé retombe
  // sur le seul élément fourni, ce qui reste juste avant tout décrochage.
  { item, items = EMPTY_ITEMS, adjustable = false }: {
    item: ScheduleItem
    items?: readonly ScheduleItem[]
    /** Rend le chrono cliquable pour ajuster les parties (régie avec pilotage). */
    adjustable?: boolean
  },
) {
  const [now, setNow] = useState(getServerNowMs)
  const [adjusting, setAdjusting] = useState(false)
  const openAdjust = useCallback(() => setAdjusting(true), [])
  const closeAdjust = useCallback(() => setAdjusting(false), [])

  useEffect(() => {
    const timer = setInterval(() => setNow(getServerNowMs()), 1000)
    return () => clearInterval(timer)
  }, [])

  // Déroulé du bloc, pas du seul segment à l'antenne : après un raccrochage, la
  // reprise ne porte aucune partie (`liveBlockRundown`).
  const rundown = useMemo(() => liveBlockRundown(item, items, now), [item, items, now])

  if (!rundown || !rundown.cursor.partCount) return null
  const { cursor, blockEndMs } = rundown
  const canAdjust = adjustable && (cursor.phase === 'PART' || cursor.phase === 'BREAK')
  // « Forcé » : la partie qui porte l'antenne (ou qui reviendra) tient une
  // durée fixée par la régie ; « ajusté » : une autre partie l'est.
  const pivot = cursor.phase === 'PART' ? cursor.current : cursor.phase === 'BREAK' ? cursor.next : null
  const forced = pivot?.adjustedSec != null
  const adjusted = !forced && rundown.segments.some(segment => segment.adjustedSec != null)

  const endingSoon = cursor.phase === 'PART'
    && cursor.remainingMs != null
    && cursor.remainingMs <= PART_ENDING_SOON_SEC * 1000

  const heading = cursor.phase === 'PART' && cursor.current
    ? partLabel(cursor.current.number, cursor.partCount, cursor.current.title)
    : cursor.phase === 'BREAK' && cursor.next
      ? `Coupure · retour partie ${cursor.next.number}/${cursor.partCount}`
      : cursor.phase === 'BEFORE' && cursor.next
        ? `Avant ${partLabel(cursor.next.number, cursor.partCount, cursor.next.title).toLowerCase()}`
        : 'Déroulé terminé'

  // Pendant la coupure on n'annonce aucune heure de retour : elle n'existe pas
  // tant que l'opérateur n'a pas raccroché. On annonce la durée qui repartira.
  const detail = cursor.phase === 'PART' && cursor.next
    ? `puis partie ${cursor.next.number}${cursor.next.title ? ` — ${cursor.next.title}` : ''} · ${cursor.next.absorbs ? `jusqu’à ${clockAt(blockEndMs)}` : minutesLabel(cursor.next.plannedSec)}`
    : cursor.phase === 'PART'
      ? `dernière partie · bloc jusqu’à ${clockAt(blockEndMs)}`
      : cursor.phase === 'BREAK' && cursor.next
        ? `${minutesLabel(cursor.next.plannedSec)} au raccrochage`
        : cursor.phase === 'BEFORE' && cursor.next
          ? cursor.remainingMs == null
            ? `${minutesLabel(cursor.next.plannedSec)} au top`
            : `top estimé à ${clockAt(cursor.next.startMs)}`
          : `bloc jusqu’à ${clockAt(blockEndMs)}`

  // Tout le chrono ouvre l'ajustement à la souris ; le bouton « Ajuster » est
  // l'accès clavier et lecteur d'écran, sans imbriquer deux contrôles.
  return (
    <>
    <section
      className="monitor-live-part"
      data-phase={cursor.phase.toLowerCase()}
      data-ending={endingSoon || undefined}
      data-adjustable={canAdjust || undefined}
      onClick={canAdjust ? openAdjust : undefined}
      title={canAdjust ? 'Ajuster le temps des parties' : undefined}
      aria-label="Déroulé estimé du direct"
    >
      <div className="monitor-live-part__copy">
        <span className="monitor-live-part__eyebrow">
          <ListOrdered size={13} aria-hidden="true" />
          {heading}
          {forced
            ? <em className="monitor-live-part__forced">forcé</em>
            : adjusted
              ? <em className="monitor-live-part__adjusted">ajusté</em>
              : <em className="monitor-live-part__estimate">estimé</em>}
          {canAdjust && (
            <button
              type="button"
              className="monitor-live-part__adjust"
              onClick={event => { event.stopPropagation(); openAdjust() }}
            >
              Ajuster
            </button>
          )}
        </span>
        <span className="monitor-live-part__detail">{detail}</span>
      </div>

      <output className="monitor-live-part__counter">
        {cursor.remainingMs == null ? '—:—' : formatCountdown(cursor.remainingMs)}
      </output>

      {/* Position dans le bloc : chaque partie à son échelle, la coupure en
          creux. La barre se lit comme celle du formulaire de saisie. */}
      <div className="monitor-live-part__bar" aria-hidden="true">
        {rundown.segments.map(segment => {
          const spanMs = Math.max(1, blockEndMs - rundown.segments[0].startMs)
          const done = Math.max(0, Math.min(1, (now - segment.startMs) / Math.max(1, segment.endMs - segment.startMs)))
          return (
            <span
              key={segment.number}
              className="monitor-live-part__bar-cell"
              data-current={cursor.current?.number === segment.number || undefined}
              style={{ width: `${((segment.endMs - segment.startMs) / spanMs) * 100}%` }}
            >
              <span className="monitor-live-part__bar-fill" style={{ width: `${done * 100}%` }} />
            </span>
          )
        })}
      </div>
    </section>
    {adjusting && <LivePartAdjustDialog item={item} items={items} onClose={closeAdjust} />}
    </>
  )
})
